import React from 'react';
import { AppProps } from 'next/app';
import { Provider } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { CacheProvider, EmotionCache } from '@emotion/react';
import parser from 'ua-parser-js';
import '../styles/globals.css';
import createEmotionCache from '../createEmotionCache';
import { wrapper } from '@/store';
import Head from 'next/head';
import Script from 'next/script';
import localFont from 'next/font/local';
import theme from '../Theme';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

export const Yekan = localFont({
  src: [
    {
      path: '../../public/fonts/yekan/YekanBakhFaNum-Regular.woff2',
      weight: '400',
      style: 'normal'
    },
    {
      path: '../../public/fonts/yekan/YekanBakhFaNum-Light.woff2',
      weight: '300',
      style: 'normal'
    },
    {
      path: '../../public/fonts/yekan/YekanBakhFaNum-SemiBold.woff2',
      weight: '600',
      style: 'normal'
    },
    {
      path: '../../public/fonts/yekan/YekanBakhFaNum-Bold.woff2',
      weight: '700',
      style: 'normal'
    }
  ]
});

// export const Yekan = localFont({
//   src: [
//     {
//       path: '../../public/fonts/yekan/YekanBakhNoEn-Thin.woff',
//       weight: '100',
//       style: 'normal'
//     },
//     {
//       path: '../../public/fonts/yekan/YekanBakhNoEn-Light.woff',
//       weight: '300',
//       style: 'normal'
//     },
//     {
//       path: '../../public/fonts/yekan/YekanBakhNoEn-Regular.woff',
//       weight: '400',
//       style: 'normal'
//     },
//     {
//       path: '../../public/fonts/yekan/YekanBakhNoEn-SemiBold.woff',
//       weight: '600',
//       style: 'normal'
//     },
//     {
//       path: '../../public/fonts/yekan/YekanBakhNoEn-Bold.woff',
//       weight: '700',
//       style: 'normal'
//     },
//     {
//       path: '../../public/fonts/yekan/YekanBakhNoEn-ExtraBold.woff',
//       weight: '800',
//       style: 'normal'
//     },
//     {
//       path: '../../public/fonts/yekan/YekanBakhNoEn-Black.woff',
//       weight: '900',
//       style: 'normal'
//     }
//   ]
// });

interface MyAppProps extends AppProps {
  deviceType?: 'mobile' | 'desktop';
  emotionCache?: EmotionCache;
}

function App({ Component, ...other }: MyAppProps) {
  const { store, props } = wrapper.useWrappedStore(other);
  const {
    emotionCache = clientSideEmotionCache,
    pageProps,
    deviceType
  } = props;

  const [mode] = React.useState<'light' | 'dark'>('light');

  const RbTheme = React.useMemo(
    () =>
      theme({
        mode,
        deviceType,
        direction: 'rtl'
      }),
    [mode, deviceType]
  );

  return (
    <Provider store={store}>
      <CacheProvider value={emotionCache}>
        <Head>
          <meta name="viewport" content="initial-scale=1, width=device-width" />
        </Head>
        <ThemeProvider theme={RbTheme}>
          <CssBaseline />
          <Component {...pageProps} />
          <Script
            id="ga"
            strategy="beforeInteractive"
            src="https://www.googletagmanager.com/gtag/js?id=G-WFMZT16JXQ"
          />
          <Script id="id-ga" strategy="beforeInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-WFMZT16JXQ');
            `}
          </Script>
          <Script id="yektanet" strategy="beforeInteractive">
            {`!function (t, e, n) { t.yektanetAnalyticsObject = n, t[n] = t[n] || function () { t[n].q.push(arguments) }, t[n].q = t[n].q || []; var a = new Date, r = a.getFullYear().toString() + "0" + a.getMonth() + "0" + a.getDate() + "0" + a.getHours(), c = e.getElementsByTagName("script")[0], s = e.createElement("script"); s.id = "ua-script-qCfAQLys"; s.dataset.analyticsobject = n; s.async = 1; s.type = "text/javascript"; s.src = "https://cdn.yektanet.com/rg_woebegone/scripts_v3/qCfAQLys/rg.complete.js?v=" + r, c.parentNode.insertBefore(s, c) }(window, document, "yektanet"); `}
          </Script>
          <Script id="goftino_ready" strategy="lazyOnload">
            {`
              var Window = window
              window.addEventListener("goftino_ready", function () {
                Window?.Goftino.setWidget({
                  hasIcon: true,
                  counter: "#unread_counter"
                });
              });
            `}
          </Script>
          <Script id="goftino" strategy="lazyOnload">
            {`
              var i = "Ai2Juj";
              var a = window;
              var d = document;
              function go() {
                var g = d.createElement("script");
                var s = "https://www.goftino.com/widget/"+i;
                var l = localStorage.getItem("goftino_"+i);
                g.async = !0;
                g.src = l ? s+"?o="+l : s;
                d.getElementsByTagName("body")[0].appendChild(g);
              }
              if (d.readyState === "complete") {
                go();
              } else if (a.attachEvent) {
                a.attachEvent("onload", go);
              } else {
                a.addEventListener("load", go, !1);
              }
            `}
          </Script>

          <Script id="id-matomo" strategy="beforeInteractive">
            {`
            var _paq = window._paq = window._paq || [];
            /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
            _paq.push(["setDocumentTitle", document.domain + "/" + document.title]);
            _paq.push(["setCookieDomain", "*.rabex.ir"]);
            _paq.push(['trackPageView']);
            _paq.push(['enableLinkTracking']);

            (function() {
              var u="//matomo.rabex.ir/";
              _paq.push(['setTrackerUrl', u+'matomo.php']);
              _paq.push(['setSiteId', '1']);
              
              var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
              g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
            })();
          `}
          </Script>
        </ThemeProvider>
      </CacheProvider>
    </Provider>
  );
}

App.getInitialProps = async (context: any) => {
  let deviceType;

  if (context.ctx.req) {
    deviceType =
      parser(context.ctx.req.headers['user-agent']).device.type || 'desktop';
  }

  return {
    deviceType
  };
};

export default App;
